import { useEffect, useState } from 'react';

import { getSubmissionActivity } from '../api/client.js';

const SEVERITY_RANK = {
  low: 1,
  Low: 1,
  medium: 2,
  Medium: 2,
  high: 3,
  High: 3,
  critical: 4,
  Critical: 4,
};

function severityDotColor(entry) {
  const oldRank = SEVERITY_RANK[entry.old_value] || 0;
  const newRank = SEVERITY_RANK[entry.new_value] || 0;
  if (newRank > oldRank) return 'var(--color-critical)';
  if (newRank < oldRank) return 'var(--color-low)';
  return '#A0AEC0';
}

function dotColor(entry) {
  switch (entry.action) {
    case 'status_change':
      return 'var(--color-primary)';
    case 'severity_change':
      return severityDotColor(entry);
    case 'assignment_change':
      return 'var(--color-teal)';
    case 'notes_updated':
      return 'var(--color-accent)';
    case 'created':
      return 'var(--color-low)';
    default:
      return '#A0AEC0';
  }
}

function actionText(entry) {
  switch (entry.action) {
    case 'status_change':
      return (
        <>
          changed Status to <strong>{entry.new_value || '—'}</strong>
        </>
      );
    case 'severity_change':
      return (
        <>
          changed Severity to <strong>{entry.new_value || '—'}</strong>
        </>
      );
    case 'assignment_change':
      return entry.new_value ? (
        <>
          assigned to <strong>{entry.new_value}</strong>
        </>
      ) : (
        <em>removed assignment</em>
      );
    case 'notes_updated':
      return 'updated Notes';
    case 'created':
      return 'Submission received';
    default:
      return `${entry.field_name} changed`;
  }
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const now = Date.now();
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.round(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

function absoluteTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function ActivityLogSkeleton() {
  return (
    <ul className="activity-log">
      {[0, 1, 2].map((i) => (
        <li key={i} className="activity-log__entry">
          <span
            className="activity-log__dot activity-log__dot--skeleton"
            aria-hidden="true"
          />
          <div className="activity-log__body">
            <div className="skeleton-cell" style={{ width: '75%', height: 12 }} />
            <div
              className="skeleton-cell"
              style={{ width: '40%', height: 10, marginTop: 6 }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function ActivityLog({ submissionId, refreshKey = 0 }) {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!submissionId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSubmissionActivity(submissionId)
      .then((data) => {
        if (!cancelled) setEntries(data.activity || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Failed to load activity');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId, refreshKey]);

  if (loading) return <ActivityLogSkeleton />;
  if (error) return <div className="form-error">{error}</div>;
  if (!entries || entries.length === 0) {
    return <div className="activity-log__empty">No activity yet</div>;
  }

  return (
    <ul className="activity-log">
      {entries.map((entry) => (
        <li key={entry.id} className="activity-log__entry">
          <span
            className="activity-log__dot"
            style={{ background: dotColor(entry) }}
            aria-hidden="true"
          />
          <div className="activity-log__body">
            <div className="activity-log__text">
              <span className="activity-log__actor">{entry.user_display}</span>{' '}
              {actionText(entry)}
            </div>
            <div
              className="activity-log__time"
              title={absoluteTime(entry.created_at)}
            >
              {relativeTime(entry.created_at)}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
